import { CarouselContent } from '../../../types/carousel'
import { SUPPORTED_PLATFORMS } from '../constants/media-platform'
import { getPosterState } from './poster-state'
import { SupportedPlatform } from '../../../types/supported-platforms'

const MEDIA_DOMAIN_VIDEO = process.env.REACT_APP_MEDIA_DOMAIN
const MEDIA_DOMAIN_POSTER = process.env.REACT_APP_MEDIA_DOMAIN_POSTER

/**
 * Detecta la plataforma a partir de una URL
 * @param url URL a evaluar
 * @returns Plataforma detectada ('media', 'instagram', 'tiktok')
 * @throws Error con mensaje descriptivo si la plataforma no es soportada
 */
export const detectPlatformFromUrl = (url: string): SupportedPlatform => {
  if (url.includes(MEDIA_DOMAIN_VIDEO as string) || url.includes(MEDIA_DOMAIN_POSTER as string)) return 'media'

  const platform = Object.values(SUPPORTED_PLATFORMS).find(p => p.match(url))
  if (!platform) {
    throw new Error(`Plataforma no soportada: ${url}`)
  }

  return platform.name as 'instagram' | 'tiktok'
}

/**
 * Detecta la plataforma a partir de una URL.
 *
 * Esta función identifica si una URL corresponde a:
 * - Un video subido internamente (plataforma `'media'`), si incluye `MEDIA_DOMAIN_VIDEO` o `MEDIA_DOMAIN_POSTER`.
 * - Un Reel de Instagram (`'instagram'`).
 * - Un video de TikTok (`'tiktok'`).
 *
 * Si la URL no pertenece a ninguna de estas plataformas, lanza un error.
 *
 * @param url URL a evaluar.
 * @returns Plataforma detectada: `'media'`, `'instagram'` o `'tiktok'`.
 */
export const isSupportedPlatformUrl = (url: string): boolean => {
  try {
    detectPlatformFromUrl(url)
    return true
  } catch {
    return false
  }
}

/**
 * Verifica si una URL pertenece a una plataforma externa soportada (Instagram o TikTok).
 * Esta función **excluye** URLs correspondientes a medios subidos internamente (por ejemplo, URLs que contienen `MEDIA_DOMAIN_VIDEO`).
 * @param url URL a evaluar.
 * @returns `true` si la URL pertenece a Instagram o TikTok, `false` en caso contrario.
 */
export const isExternalPlatformUrl = (url: string): boolean => {
  try {
    const platform = detectPlatformFromUrl(url)
    return platform === 'instagram' || platform === 'tiktok'
  } catch {
    return false
  }
}

/**
 * Genera dominio CDN para videos subidos manualmente
 * @param fileKey video ID
 * @returns Dominio CDN video
 */
export const getVideoCdnUrl = (fileKey: string): string => {
  const normalizedKey = fileKey.replace(/^uploads\//, '').replace(/\.mp4$/, '.m3u8')

  return `${MEDIA_DOMAIN_VIDEO}/${normalizedKey}`
}

/**
 * Genera dominio CDN para posters subidos manualmente
 * @param fileKey poster ID
 * @returns Dominio CDN poster
 */
export const getPosterCdnUrl = (fileKey: string): string => {
  const cleanedKey = fileKey.replace(/^uploads\//, '').replace(/\.[^.]+$/, '.jpeg')
  return `${MEDIA_DOMAIN_POSTER}/${cleanedKey}`
}

/**
 * Remueve del path la carpeta 'optimized'
 * @param url
 * @returns Path sin la carpeta 'optimized'
 */
export const removeOptimizedPath = (url: string): string => {
  return url.replace(/\/optimized\//, '/')
}

// Función para extraer URLs que necesitan metadatos
/**
 * Verifica si el contenido de un carrusel necesita metadatos
 * @param content
 * @returns URLs que necesitan metadatos
 */
export const extractUrlsNeedingMetadata = (content: CarouselContent[]): string[] => {
  const itemsNeedingMetadata = content.filter(item => getPosterState(item) !== 'ready')

  return itemsNeedingMetadata
    .filter(
      item =>
        item.link && (SUPPORTED_PLATFORMS.instagram.match(item.link) || SUPPORTED_PLATFORMS.tiktok.match(item.link)),
    )
    .map(item => item.link as string)
}
